<app-uni-page [parent]="this" pageTitle="Manufacturer Feed" [bodyTemplate]="bodyTemplate" [footerTemplate]="footerTemplate"/>

<ng-template #bodyTemplate>
  <div class="flex flex-col h-full" *ngIf="isPageReady">

    <!-- items-end, not items-center: every uni-field carries a label above its input, so centering
         lines the buttons up against the label+input block and they ride high above the inputs. -->
    <div class="qb-list-actions flex items-end gap-3 flex-wrap">
      <uni-field label="Manufacturer">
        <p-dropdown [options]="manufacturers" [(ngModel)]="manufacturerId" optionValue="Id" optionLabel="Label"
                    [style]="{ 'min-width': '260px' }" (onChange)="onManufacturerChange()" />
      </uni-field>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-refresh"
              [loading]="isLoading" [disabled]="isLoading || manufacturerId == null" (click)="refresh()">
        <span>Refresh</span>
      </button>

      <span class="border-l h-8 mx-1"></span>

      <uni-field label="Style">
        <input pInputText title="Style" [(ngModel)]="singleStyle" placeholder="Enter style..."
               [style]="{ width: '140px' }" (keyup.enter)="stageOneStyle()" />
      </uni-field>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-download"
              [loading]="isStaging" [disabled]="isStaging || !singleStyle || manufacturerId == null"
              (click)="stageOneStyle()">
        <span>Read One Style</span>
      </button>

      <span class="border-l h-8 mx-1"></span>

      <uni-field label="Style Limit">
        <p-inputNumber [(ngModel)]="maxStyles" [min]="1" [max]="5000" [showButtons]="false"
                       [style]="{ width: '90px' }" title="Style Limit" />
      </uni-field>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-cloud-download"
              [disabled]="isStaging || manufacturerId == null" (click)="startSweep()">
        <span>Read All Styles</span>
      </button>

      <span class="border-l h-8 mx-1"></span>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-palette"
              [disabled]="isStaging || manufacturerId == null" (click)="openBulkRepoint()"
              title="Move one color to another across every style of this manufacturer">
        <span>Change a Color Everywhere</span>
      </button>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-arrows-h"
              [disabled]="isStaging || manufacturerId == null" (click)="openSizeMapping()"
              title="Say what each size the manufacturer publishes means in Uniformax">
        <span>Match Sizes</span>
      </button>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-ban"
              [disabled]="isStaging || manufacturerId == null" (click)="openBulkRetire()"
              title="Retire the styles and products the manufacturer no longer makes">
        <span>Retire Unavailable Products</span>
      </button>

      <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-file-excel"
              [disabled]="isStaging || manufacturerId == null" (click)="openErrorAnalysis()"
              title="Every style the manufacturer did not return, with its error">
        <span>Export Errors</span>
      </button>

      <span *ngIf="statusMessage" class="text-sm">{{ statusMessage }}</span>

      <!-- h-9 matches .p-button-sm (36px) so the checkbox centre lines up with the button centres. -->
      <div class="ml-auto self-end h-9 flex items-center gap-4">
        <!-- severity, not a p-button-danger class: the Button directive's setStyleClass strips
             any severity class it did not put there and rebuilds from this input. -->
        <!-- The only way to turn a feed off, so it sits apart from the tools that align a feed.
             Off until the top authorization level exists; see canRemoveFeedStyles. -->
        <button pButton type="button" class="p-button-outlined p-button-sm" severity="danger"
                icon="pi pi-trash"
                [disabled]="!canRemoveFeedStyles || isStaging || isRemoving || manufacturerId == null || !user.isProductAdmin"
                [loading]="isRemoving" (click)="onRemoveFeedStyles()"
                [title]="canRemoveFeedStyles
                  ? 'Delete the feed styles read for this manufacturer and turn its feed off'
                  : 'This action is off until a higher authorization level is available.'">
          <span>Remove Feed Styles</span>
        </button>

        <div class="flex items-center gap-2">
          <p-checkbox inputId="showOnlyNeedingAttention" [(ngModel)]="showOnlyNeedingAttention" [binary]="true"
                      (onChange)="applyStyleFilter()"></p-checkbox>
          <label for="showOnlyNeedingAttention" class="text-sm">Show only styles needing attention</label>
        </div>
      </div>
    </div>

    <div *ngIf="isFeedOff" class="px-4 pb-2 shrink-0 text-sm" style="color: var(--p-red-500, #ef4444)">
      The feed status of {{ selectedManufacturerName }} is None, but its feed styles are still stored.
      Ask the system administrator to remove them.
    </div>

    <div *ngIf="!isConfigured" class="px-4 pb-2 shrink-0 text-sm" style="color: var(--p-red-500, #ef4444)">
      {{ configurationHint }}
    </div>

    <!-- Snapshot marker. Which pull this data came from, and how it went. -->
    <div *ngIf="latestRun" class="px-4 pt-3 pb-2 shrink-0">
      <div class="qb-detail-card qb-primary-tint px-4 py-2">
        <div class="font-semibold text-base mb-1 border-b pb-1 uppercase tracking-wide">
          Last Read &mdash; {{ latestRun.StartedTs | date: 'short' }}
          <span *ngIf="!latestRun.IsComplete" class="ml-2 normal-case font-normal">
            <i class="pi pi-spin pi-spinner"></i> in progress
          </span>
        </div>

        <!-- Live progress. A sweep is one call per style and 478 styles take the best part of an
             hour, so the page says where it is up to rather than leaving someone guessing. -->
        <div *ngIf="!latestRun.IsComplete && sweepTotal > 0" class="mb-3">
          <div class="flex items-center gap-3 mb-1">
            <span class="font-semibold text-lg">{{ sweepDone }} of {{ sweepTotal }}</span>
            <span class="text-sm" style="color: var(--p-text-muted-color)">styles read</span>

            <span *ngIf="latestRun.StylesFailed > 0" class="text-sm font-semibold"
                  style="color: var(--p-red-500, #ef4444)">
              {{ latestRun.StylesFailed }} failed
            </span>

            <span class="ml-auto text-sm" style="color: var(--p-text-muted-color)">
              {{ sweepElapsed }} elapsed
              <ng-container *ngIf="sweepRemaining">
                &middot; about {{ sweepRemaining }} left
              </ng-container>
              <ng-container *ngIf="sweepRate">
                &middot; {{ sweepRate }} / min
              </ng-container>
            </span>
          </div>

          <p-progressBar [value]="sweepPercent" [showValue]="true" [style]="{ height: '18px' }"></p-progressBar>

          <div class="mt-1 text-sm" style="color: var(--p-text-muted-color)">
            The sweep runs on the server. You can leave this page and come back.
          </div>
        </div>
        <table class="qb-info-table w-full">
          <tr class="font-semibold">
            <td class="value text-right">Styles asked for</td>
            <td class="value text-right">Read</td>
            <td class="value text-right">Failed</td>
            <td class="value text-right">Colors</td>
            <td class="value text-right">Parts</td>
            <td class="value text-right">Finished</td>
          </tr>
          <tr>
            <td class="value text-right">{{ latestRun.StylesRequested }}</td>
            <td class="value text-right">{{ latestRun.StylesSucceeded }}</td>
            <td class="value text-right">{{ latestRun.StylesFailed }}</td>
            <td class="value text-right">{{ latestRun.ColorsStaged }}</td>
            <td class="value text-right">{{ latestRun.PartsStaged }}</td>
            <td class="value text-right">{{ latestRun.CompletedTs ? (latestRun.CompletedTs | date: 'short') : '-' }}</td>
          </tr>
        </table>
        <div *ngIf="latestRun.Error" class="mt-2 text-sm" style="color: var(--p-red-500, #ef4444)">
          {{ latestRun.Error }}
        </div>
      </div>
    </div>

    <div class="flex-1 min-h-0 flex flex-col gap-2">
      <!-- The styles take the whole height when the manufacturer has no color question. -->
      <div
        class="flex flex-col overflow-hidden"
        [style.flex]="showColorPanels ? '0 0 45%' : '1 1 auto'"
        [class.min-h-0]="!showColorPanels"
      >
        <div class="qb-child-actions shrink-0">
          <span class="text-sm font-semibold">Styles ({{ filteredStyles.length }})</span>
        </div>
        <app-ag-grid [gridOptions]="styleGridOptions" [rowData]="filteredStyles" class="flex-1 min-h-0"></app-ag-grid>
      </div>

      <div *ngIf="showColorPanels" class="flex flex-col overflow-hidden flex-1 min-h-0">
        <div *ngIf="!detail" class="p-4 text-sm italic" style="color: var(--p-text-muted-color, #6b7280)">
          Select a style to see its colors.
        </div>

        <ng-container *ngIf="detail">
          <div class="qb-child-actions shrink-0 flex items-center gap-4 flex-wrap">
            <span class="text-sm font-semibold">
              {{ detail.Style.Style }} &mdash; {{ detail.Style.ProductName }}
            </span>
            <span class="text-sm">
              Manufacturer colors: {{ detail.Style.StagedColorCount }} &nbsp;|&nbsp;
              Uniformax colors: {{ detail.Style.UniformaxColorCount }} &nbsp;|&nbsp;
              Matched: {{ detail.Style.MatchedColorCount }}
            </span>
            <span *ngIf="detail.Style.UnmatchedUniformaxColorCount > 0" class="text-sm font-semibold"
                  style="color: var(--p-red-500, #ef4444)">
              Not in the feed: {{ detail.Style.UnmatchedUniformaxColorCount }}
            </span>
            <a *ngIf="detail.Style.PrimaryImageUrl" [href]="detail.Style.PrimaryImageUrl" target="_blank"
               class="text-sm">Manufacturer image</a>
          </div>

          <div class="flex-1 min-h-0 flex flex-row gap-2">
            <div class="flex flex-col overflow-hidden" style="flex: 1 1 68%">
              <div class="qb-child-actions shrink-0">
                <span class="text-sm">What the manufacturer publishes</span>
              </div>
              <app-ag-grid [gridOptions]="colorGridOptions" [rowData]="detail.StagedColors"
                           class="flex-1 min-h-0"></app-ag-grid>
            </div>

            <div class="flex flex-col overflow-hidden" style="flex: 1 1 32%">
              <div class="qb-child-actions shrink-0">
                <span class="text-sm">Uniformax colors with no match</span>
              </div>
              <app-ag-grid [gridOptions]="unmatchedGridOptions" [rowData]="detail.UnmatchedUniformaxColors"
                           class="flex-1 min-h-0"></app-ag-grid>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #footerTemplate>
  <div class="left-buttons">
    <button pButton type="button" class="p-button-raised" (click)="goBack()"><span>Go Back</span></button>
  </div>
</ng-template>
